import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell } from "lucide-react";

import useLanguageTranslate from "../../hooks/useLanguageTranslate";
import useScreenResolution from "../../hooks/useScreenResolution";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "../../components/ui/sheet";
import { cn } from "../../lib/utils";
import NotificationsPanel from "./NotificationsPanel";

/**
 * The header bell and its inbox.
 *
 * Two shells, one body. Below 768px the inbox is a bottom sheet — thumb
 * reachable, full-width, and direction-agnostic, which matters because the bell
 * itself moves from the right of the header to the left in Arabic. From 768px
 * up it is a popover anchored under the bell: a notifications list is a glance,
 * not a task, so it should not take the screen hostage the way a modal does.
 *
 * Radix owns the interaction contract in both: Esc to dismiss, outside click,
 * focus trapped while open, focus returned to the bell on close.
 */
/**
 * No count badge. It showed the number of locally-unread notifications, and
 * with no read state to derive it from the only honest alternatives are a badge
 * that always shows the same total — permanent noise that stops meaning
 * anything after the first look — or none. If the API grows a read flag, the
 * badge comes back with it.
 */
const NotificationTrigger = React.forwardRef(({ translate, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-label={translate("Notifications", "الإشعارات")}
    className={cn(
      "relative inline-flex size-9 items-center justify-center rounded-full text-[#5B6871]",
      "transition-colors duration-150 hover:bg-[#F6F8F9] hover:text-[#1A2024]",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      "data-[state=open]:bg-[#F6F8F9] data-[state=open]:text-[#1A2024]"
    )}
    {...props}
  >
    <Bell className="size-5" aria-hidden="true" strokeWidth={1.75} />
  </button>
));
NotificationTrigger.displayName = "NotificationTrigger";

const NotificationBell = () => {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const { mobile } = useScreenResolution();
  const [open, setOpen] = useState(false);
  const titleId = useId();

  const isRtl = i18n.resolvedLanguage === "ar";

  if (mobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <NotificationTrigger translate={translate} />
        </SheetTrigger>
        <SheetContent
          side="bottom"
          data-notifications-surface=""
          aria-labelledby={titleId}
          // Radix warns when a Dialog has neither a Description nor an explicit
          // opt-out. The heading and the list are the whole content here, so
          // there is nothing a description would add that isn't already read.
          aria-describedby={undefined}
          // Fits its content rather than always claiming 88svh, so four
          // notifications don't sit above a screen of dead space. The floor
          // keeps the empty and error states from collapsing into a sliver, and
          // the ceiling hands scrolling to the list once it gets long.
          className="max-h-[88svh] min-h-[42svh] rounded-t-2xl p-0"
        >
          {/* Grabber: signals the sheet is dismissable by drag-down intent,
              which is what a mobile user reaches for before hunting a button. */}
          <div className="flex justify-center pt-2.5" aria-hidden="true">
            <span className="h-1 w-9 rounded-full bg-border" />
          </div>
          <NotificationsPanel
            titleId={titleId}
            TitleComponent={SheetTitle}
            onClose={() => setOpen(false)}
          />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <NotificationTrigger translate={translate} />
      </PopoverTrigger>
      <PopoverContent
        // Radix aligns along the physical axis. In Arabic the bell sits at the
        // left of the header, so anchoring to "end" would push the panel off
        // toward the far side of the viewport.
        align={isRtl ? "start" : "end"}
        data-notifications-surface=""
        aria-label={translate("Notifications", "الإشعارات")}
        className="flex max-h-[min(70vh,560px)] w-[min(400px,calc(100vw-24px))] flex-col p-0"
      >
        <NotificationsPanel titleId={titleId} />
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
